import { format, formatISO } from 'date-fns';

import {
  type AnimalEditValues,
  type AnimalRow,
  buildEditAnimalInput,
  type EditAnimalInput,
  type ProcedureRequest,
} from '@/services/animal-management';

export type BulkEditValues = {
  ownerId: string | null;
  yardId: string | null;
  penId: string | null;
  status: string;
  weight?: number;
  opuProgram: string;
  birthDate?: Date;
};

const DONOR_TYPES = new Set(['Donor', 'LOPU Donor']);

/**
 * Splits the form into per-section changes and builds one PUT body per animal.
 * Donor-section fields only go to donors and calf-section fields only to
 * calves; each body is then trimmed to what that animal's table can store.
 */
export function buildBulkEditInputs(
  animals: AnimalRow[],
  values: BulkEditValues,
): { inputs: EditAnimalInput[]; skipped: AnimalRow[] } {
  const common: Partial<AnimalEditValues> = {};
  if (values.ownerId) common.ownerId = values.ownerId;
  if (values.yardId) common.yardId = values.yardId;
  if (values.status === 'active' || values.status === 'inactive') {
    common.status = values.status;
  }
  if (typeof values.weight === 'number') common.weight = values.weight;

  const donor: Partial<AnimalEditValues> = {};
  if (values.opuProgram.trim()) donor.opuProgram = values.opuProgram.trim();

  const calf: Partial<AnimalEditValues> = {};
  if (values.birthDate) {
    calf.birthDate = format(values.birthDate, 'yyyy-MM-dd');
  }
  if (values.penId) calf.penId = values.penId;

  const inputs: EditAnimalInput[] = [];
  const skipped: AnimalRow[] = [];
  for (const animal of animals) {
    const changes = {
      ...common,
      ...(DONOR_TYPES.has(animal.animalType) ? donor : {}),
      ...(animal.animalType === 'Calf' ? calf : {}),
    };
    const { input } = buildEditAnimalInput(animal, changes);
    if (input) inputs.push(input);
    else skipped.push(animal);
  }
  return { inputs, skipped };
}

/**
 * One POST per animal per procedure. The performer is only sent when it is
 * someone other than the current user; otherwise the API defaults it to the
 * caller (#25588 Scenario 2).
 */
export function buildProcedureRequests(
  animals: Pick<AnimalRow, 'id'>[],
  procedures: Array<{
    procedureCatalogId: string;
    description: string;
    enteredById: string;
    date?: Date;
  }>,
  currentUserId: string | undefined,
): ProcedureRequest[] {
  return animals.flatMap((animal) =>
    procedures.map((procedure) => ({
      animalId: animal.id,
      input: {
        procedureCodeId: procedure.procedureCatalogId,
        procedureDate: formatISO(procedure.date!, { representation: 'date' }),
        description: procedure.description.trim() || undefined,
        performedById:
          procedure.enteredById && procedure.enteredById !== currentUserId
            ? procedure.enteredById
            : undefined,
      },
    })),
  );
}
